// Events are prefixed automatically by the AiTeammateAnalyticsProvider
export const AI_TEAMMATE_EVENTS = {
  /**
   * Kelly's dock emits these too, keeping the drawer's wire names on purpose: they are what the existing
   * adoption dashboards filter on, and `contextPrefixes` already separates the two surfaces. Same reasoning
   * as the nav rail keeping its "agent-chat" menu key after the route became `kelly-ai`.
   */
  DRAWER_OPENED: "Drawer Opened",
  DRAWER_CLOSED: "Drawer Closed",
  NEW_THREAD_CREATED: "New Thread Created",
  THREAD_RESET: "Thread Reset",
  MESSAGE_SENT: "Message Sent",
  // { theme: string, prompt_id: string, surface: "empty_state" | "collapsed" }
  SUGGESTED_PROMPT_SHOWN: "Suggested Prompt Shown",
  // { theme: string, prompt_id: string }
  SUGGESTED_PROMPT_CLICKED: "Suggested Prompt Clicked",
  // { source: "suggested_prompt" | "nudge" | "typed", promptId?: string, theme?: string }
  CHAT_MESSAGE_SENT: "Chat Message Sent",
  // { theme: string }
  CATEGORY_EXPANDED: "Category Expanded",
  PROMPTING_GUIDE_OPENED: "Prompting Guide Opened",
  // Discoverability nudge anchored to the chat button (once per session)
  NUDGE_SHOWN: "Nudge Shown",
  // { reason: "dismissed" | "opened_chat" | "question_clicked" }
  NUDGE_DISMISSED: "Nudge Dismissed",
  // { prompt_id: string }
  NUDGE_QUESTION_CLICKED: "Nudge Question Clicked",
  PLOT_EXPANDED: "Plot Expanded",
  TABLE_EXPANDED: "Table Expanded",
  TABLE_EXPLAIN_OPENED: "Table Explain Opened",
  // { artifactId, artifactType, source: "card" | "restored", threadId? } — "card" = an open the user asked for;
  // "restored" = the panel re-deriving an artifact that was never closed (a there-and-back thread switch).
  ARTIFACT_VIEWED: "Artifact Viewed",
  ARTIFACT_DOWNLOADED: "Artifact Downloaded",
  // An export the artifact built in the browser and the user confirmed (APP-3495).
  // { artifactId, threadId, mediaType, sizeBytes }
  ARTIFACT_EXPORT_SAVED: "Artifact Export Saved",
  // { artifactId, threadId, reason: "media-type" | "too-large" | "shape" | "cancelled" | "unbrokered" }
  // `unbrokered` is the shim reporting a download it could not intercept — the signal APP-3535 filters on.
  ARTIFACT_EXPORT_REFUSED: "Artifact Export Refused",
  ARTIFACT_PIN_INITIATED: "Artifact Pin Initiated",
  ARTIFACT_PIN_SUCCESS: "Artifact Pin Success",
  ARTIFACT_PIN_FAILED: "Artifact Pin Failed",
  // The events below come from the full-page Kelly chat (APP-3438).
  // { surface: PanelSurface, auto_collapsed?: true } — `surface` says WHICH conversations list opened or closed
  // (the page's own panel, or the dock's flyout); `auto_collapsed` says an opening artifact did it, not the user.
  PANEL_COLLAPSED: "Panel Collapsed",
  PANEL_EXPANDED: "Panel Expanded",
  // Conversations shown on hover over the collapsed rail
  RECENTS_PREVIEW_OPENED: "Recents Preview Opened",
  THREAD_OPENED: "Thread Opened",
  ARTIFACT_CLOSED: "Artifact Closed",
  // `expanded` reports the state after the toggle, not before
  ARTIFACT_FULLSCREEN_TOGGLED: "Artifact Fullscreen Toggled",
  // The DOCK's own full screen, distinct from the artifact panel's above (APP-3572).
  // { expanded: boolean } — the state after the toggle, not before.
  DOCK_FULLSCREEN_TOGGLED: "Dock Fullscreen Toggled",
  // A width the user committed by dragging; the artifact-driven auto-widen deliberately does not emit.
  // { widthPx: number }
  DOCK_RESIZED: "Dock Resized",
  // A turn that reached an answer (APP-3572). { durationMs, transport: "streaming" | "buffered" }
  // Together with Chat Message Sent this is the completion rate; on its own it is Kelly's latency.
  TURN_COMPLETED: "Turn Completed",
  // The composer's Stop. Reported at the button rather than off the abort, because a nav-away aborts too.
  TURN_STOPPED: "Turn Stopped",
  // The artifact panel's Copy-data control (APP-3572). { artifactId, artifactType }
  ARTIFACT_COPIED: "Artifact Copied",
  // A width the user committed by dragging the artifact panel's edge. { widthPx }
  ARTIFACT_PANEL_RESIZED: "Artifact Panel Resized",
  // Sharing a file artifact with colleagues (AI-734). Reported when a grant is applied, not when the
  // dialog opens — opening it commits nothing.
  // { artifactId, resourceId, recipientCount, recipientKind: "member" | "team" | "mixed",
  //   recipientUserIds: string[], recipientTeamIds: string[] }
  // The ids are the point: whether sharing pulls colleagues into Kelly is the question this is for,
  // and a count alone cannot answer it.
  ARTIFACT_SHARED: "Artifact Shared",
  // One recipient losing access. { artifactId, resourceId, recipientKind: "member" | "team", recipientId }
  ARTIFACT_UNSHARED: "Artifact Unshared",
  // { artifactId, resourceId } — a link is a share too, and it creates the resource if none existed.
  ARTIFACT_SHARE_LINK_COPIED: "Artifact Share Link Copied",
  // Opened from the Artifacts list rather than from a conversation (AI-734). Distinct from Artifact Viewed,
  // which the panel emits either way — this one says the list is how people find their work again.
  // { artifactId, resourceId, filter: "all" | "yours" | "shared", isOwn }
  ARTIFACT_OPENED_FROM_LIST: "Artifact Opened From List",
  // { filter } — which pill the user switched to.
  ARTIFACTS_FILTERED: "Artifacts Filtered",
  // { surface: "page_panel" | "dock_flyout" } — the Artifacts nav item in Kelly's side panel.
  ARTIFACTS_SURFACE_OPENED: "Artifacts Surface Opened",
  // Conversation-list management (APP-3572). Generic names; the payload says which list and which way.
  // All carry `surface`: the list renders in three places under these same event names.
  THREAD_RENAMED: "Thread Renamed",
  // { pinned } — the state after the toggle, so pin and unpin are one series with a direction.
  THREAD_PINNED: "Thread Pinned",
  THREAD_DELETED: "Thread Deleted",
  // Reported once per settled query rather than per keystroke. { hasMatch }
  THREADS_SEARCHED: "Threads Searched",
  // { section: "pinned" | "recents", collapsed }
  THREAD_SECTION_TOGGLED: "Thread Section Toggled",
  // The reader took an answer away — the strongest signal a turn was any good. { role }
  MESSAGE_COPIED: "Message Copied",
  // An in-message disclosure. { kind: "thinking_trail" | "tool_step", open }
  // No `working_files` yet: that disclosure is data-gated on an `intermediate` flag the wire doesn't emit
  // (see working-files.tsx), so it renders dark and the kind would document a value no dashboard could receive.
  DISCLOSURE_TOGGLED: "Disclosure Toggled",
} as const

/**
 * Which conversations list a `Panel Collapsed`/`Panel Expanded` describes. The two lists are the same feature in
 * two places, so they keep one event name and are told apart by this — three call sites otherwise shared two
 * event names with three different payloads and no field that separated them in a query.
 */
export const THREAD_LIST_SURFACES = {
  /** The full-page chat's conversations panel. */
  PAGE: "page_panel",
  /** The dock's conversations flyout. */
  DOCK_FLYOUT: "dock_flyout",
  /** The hover peek over the collapsed rail or the dock header. */
  RECENTS_PREVIEW: "recents_preview",
} as const

export type ThreadListSurface = typeof THREAD_LIST_SURFACES[keyof typeof THREAD_LIST_SURFACES]

/** Payload-shaped for `track(EVENT, PANEL_SURFACES.X)`; derived so there is one surface vocabulary. */
export const PANEL_SURFACES = {
  PAGE: { surface: THREAD_LIST_SURFACES.PAGE },
  DOCK_FLYOUT: { surface: THREAD_LIST_SURFACES.DOCK_FLYOUT },
} as const

export type AiTeammateEventName = typeof AI_TEAMMATE_EVENTS[keyof typeof AI_TEAMMATE_EVENTS]
